import React, { useState, useEffect } from 'react';
import {
    TextField,
    Button,
    styled,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Paper,
    Divider,
    Grid,
    Avatar
} from '@mui/material';
import Sidebar from '../components/Sidebar';
import config from '../config';
import tgaModes from '../config/tgaModes';

const ContentContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
});

const FormContainer = styled('form')({
    display: 'flex',
    marginTop: '2rem',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'stretch',
});

const Header = styled('h1')(({ theme }) => ({
    fontSize: '1.65rem',
    width: '100%',
    fontWeight: 'normal',
    fontFamily: theme.typography.fontFamily,
    color: '#2E2E2E',
    paddingTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const FormElement = styled('div')({
    margin: '1rem 0'
});

const PageContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignContent: 'center',
    width: '100%',
    height: '100%',
});

const FileUploadSection = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px dashed ${theme.palette.primary.main}`,
    backgroundColor: '#fafafa',
}));

const ModeRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
});

const TGAImagesUploadPage = () => {
    const [productId, setProductId] = useState('');
    const [modes] = useState(tgaModes);
    const [filesByMode, setFilesByMode] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [loadingModes] = useState(false);
    const [existingByMode, setExistingByMode] = useState({});
    const [deleting, setDeleting] = useState({});

    // Load existing images for productId
    useEffect(() => {
        const pid = parseInt(productId, 10);
        if (!productId || Number.isNaN(pid)) {
            setExistingByMode({});
            return;
        }
        let aborted = false;
        (async () => {
            try {
                const res = await fetch(`${config.serverUrl}/context-mode-images/${pid}`);
                const data = await res.json();
                if (!res.ok) throw new Error(data.detail || 'Failed to load existing images');
                if (aborted) return;
                const map = {};
                (data.images || []).forEach(it => {
                    map[it.context_mode_id] = it;
                });
                setExistingByMode(map);
            } catch (e) {
                if (!aborted) setExistingByMode({});
            }
        })();
        return () => { aborted = true; };
    }, [productId]);

    const onFileChange = (modeId, file) => {
        if (!file) {
            setFilesByMode(prev => {
                const next = { ...prev };
                delete next[modeId];
                return next;
            });
            return;
        }
        // Accept TGA and images
        const ok = file.type.startsWith('image/') || file.name.toLowerCase().endsWith('.tga');
        if (!ok) {
            setMessage({ type: 'error', text: 'Only image files (including .tga) are allowed.' });
            return;
        }
        setFilesByMode(prev => ({ ...prev, [modeId]: file }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!productId.trim()) {
            setMessage({ type: 'error', text: 'Please enter a product ID.' });
            return;
        }
        const entries = Object.entries(filesByMode);
        if (entries.length === 0) {
            setMessage({ type: 'error', text: 'Select at least one image to upload.' });
            return;
        }

        setLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const form = new FormData();
            const ids = [];
            entries.forEach(([modeId, file]) => {
                form.append('images', file);
                ids.push(parseInt(modeId, 10));
            });

            const params = new URLSearchParams();
            params.append('product_id', parseInt(productId, 10));
            ids.forEach(id => params.append('context_mode_ids', id));

            const res = await fetch(`${config.serverUrl}/upload-tga-images?${params.toString()}`, {
                method: 'POST',
                body: form,
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.detail || 'Upload failed');
            }

            setMessage({ type: 'success', text: 'Images uploaded successfully.' });
            setFilesByMode({});
                // Refresh existing previews
                try {
                    const pid = parseInt(productId, 10);
                    if (!Number.isNaN(pid)) {
                        const reload = await fetch(`${config.serverUrl}/context-mode-images/${pid}`);
                        const json = await reload.json();
                        if (reload.ok) {
                            const map = {};
                            (json.images || []).forEach(it => { map[it.context_mode_id] = it; });
                            setExistingByMode(map);
                        }
                    }
                } catch {}
        } catch (err) {
            setMessage({ type: 'error', text: err.message || 'Upload failed' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (modeId) => {
        const pid = parseInt(productId, 10);
        if (!productId || Number.isNaN(pid)) return;
        setDeleting(prev => ({ ...prev, [modeId]: true }));
        try {
            const res = await fetch(`${config.serverUrl}/context-mode-images`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ product_id: pid, context_mode_id: modeId })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || 'Delete failed');
            setMessage({ type: 'success', text: 'Image deleted.' });
            // refresh
            try {
                const reload = await fetch(`${config.serverUrl}/context-mode-images/${pid}`);
                const json = await reload.json();
                if (reload.ok) {
                    const map = {};
                    (json.images || []).forEach(it => { map[it.context_mode_id] = it; });
                    setExistingByMode(map);
                }
            } catch {}
        } catch (e) {
            setMessage({ type: 'error', text: e.message || 'Delete failed' });
        } finally {
            setDeleting(prev => ({ ...prev, [modeId]: false }));
        }
    };

    return (
        <PageContainer>
            <Sidebar />
            <ContentContainer>
                <Header>Upload TGA Images (Context Modes)</Header>

                {message.text && (
                    <Alert severity={message.type} sx={{ mb: 2 }}>
                        {message.text}
                    </Alert>
                )}

                <FormContainer onSubmit={handleSubmit}>
                    <FormElement>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Product ID"
                                    type="number"
                                    value={productId}
                                    onChange={(e) => setProductId(e.target.value)}
                                    required
                                    disabled={loading}
                                    fullWidth
                                    placeholder="Enter product ID"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} />
                        </Grid>
                    </FormElement>

                    <Divider sx={{ my: 2 }} />

                    {modes.length > 0 ? (
                        <Box>
                            {productId && Object.keys(existingByMode).length === 0 && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    No images found for this product. Upload images for the context modes below.
                                </Alert>
                            )}
                            {modes.map((m) => (
                                <FileUploadSection key={`${m.id}`}>
                                    <ModeRow>
                                        <Avatar src={m.image || ''} variant="rounded" sx={{ width: 64, height: 64 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1">{m.title || `Mode ${m.id}`}</Typography>
                                            <Typography variant="body2" color="textSecondary">ContextModeId: {m.id}</Typography>
                                            {existingByMode[m.id] ? (
                                                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="caption" color="textSecondary">Current:</Typography>
                                                    <Avatar
                                                        src={(existingByMode[m.id].thumb && existingByMode[m.id].thumb.url) || existingByMode[m.id].original_url || ''}
                                                        variant="rounded"
                                                        sx={{ width: 48, height: 48 }}
                                                    />
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                        {existingByMode[m.id].timestamp && (
                                                            <Typography variant="caption" color="textSecondary">
                                                                Uploaded: {new Date(existingByMode[m.id].timestamp).toLocaleString()}
                                                            </Typography>
                                                        )}
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            variant="text"
                                                            onClick={() => handleDelete(m.id)}
                                                            disabled={!!deleting[m.id] || loading}
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            {deleting[m.id] ? 'Deleting...' : 'Delete'}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            ) : productId && (
                                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                                    No image uploaded for this mode
                                                </Typography>
                                            )}
                                        </Box>
                                        <input
                                            accept="image/*,.tga"
                                            style={{ display: 'none' }}
                                            id={`upload-${m.id}`}
                                            type="file"
                                            onChange={(e) => onFileChange(m.id, e.target.files && e.target.files[0])}
                                        />
                                        <label htmlFor={`upload-${m.id}`}>
                                            <Button variant="outlined" component="span" disabled={loading}>
                                                {filesByMode[m.id] ? filesByMode[m.id].name : 'Select Image'}
                                            </Button>
                                        </label>
                                        {filesByMode[m.id] && (
                                            <Button
                                                size="small"
                                                color="error"
                                                disabled={loading}
                                                onClick={() => onFileChange(m.id, null)}
                                                sx={{ ml: 1 }}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </ModeRow>
                                </FileUploadSection>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            {loadingModes ? 'Loading modes...' : 'Load context modes to display upload fields.'}
                        </Typography>
                    )}

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || Object.keys(filesByMode).length === 0 || !productId.trim()}
                        sx={{ mt: 2, py: 1.5 }}
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Upload TGA Images'
                        )}
                    </Button>
                </FormContainer>
            </ContentContainer>
        </PageContainer>
    );
};

export default TGAImagesUploadPage;
